"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Copy, Check, Trash2 } from "lucide-react"
import { toast } from "sonner"

interface UnitValue {
  label: string
  shortLabel: string
  bytes: number // multiplier to convert to bytes
  value: string
}

// Binary (base 2) units - IEC standard
const binaryUnits: UnitValue[] = [
  { label: "Bytes", shortLabel: "B", bytes: 1, value: "" },
  { label: "Kibibytes", shortLabel: "KiB", bytes: 1024, value: "" },
  { label: "Mebibytes", shortLabel: "MiB", bytes: 1024 ** 2, value: "" },
  { label: "Gibibytes", shortLabel: "GiB", bytes: 1024 ** 3, value: "" },
  { label: "Tebibytes", shortLabel: "TiB", bytes: 1024 ** 4, value: "" },
  { label: "Pebibytes", shortLabel: "PiB", bytes: 1024 ** 5, value: "" },
]

// Decimal (base 10) units - SI standard
const decimalUnits: UnitValue[] = [
  { label: "Bytes", shortLabel: "B", bytes: 1, value: "" },
  { label: "Kilobytes", shortLabel: "KB", bytes: 1000, value: "" },
  { label: "Megabytes", shortLabel: "MB", bytes: 1000 ** 2, value: "" },
  { label: "Gigabytes", shortLabel: "GB", bytes: 1000 ** 3, value: "" },
  { label: "Terabytes", shortLabel: "TB", bytes: 1000 ** 4, value: "" },
  { label: "Petabytes", shortLabel: "PB", bytes: 1000 ** 5, value: "" },
]

// Bit units
const bitUnits: UnitValue[] = [
  { label: "Bits", shortLabel: "b", bytes: 0.125, value: "" },
  { label: "Kilobits", shortLabel: "Kb", bytes: 125, value: "" },
  { label: "Megabits", shortLabel: "Mb", bytes: 125000, value: "" },
  { label: "Gigabits", shortLabel: "Gb", bytes: 125000000, value: "" },
]

function formatNumber(num: number): string {
  if (num === 0) return "0"
  if (num < 0.0001) return num.toExponential(4)
  if (num >= 1e15) return num.toExponential(4)

  // Format with appropriate precision
  if (Number.isInteger(num)) return num.toLocaleString()

  const precision = num < 1 ? 8 : num < 100 ? 6 : 4
  return parseFloat(num.toPrecision(precision)).toLocaleString(undefined, {
    maximumFractionDigits: 10,
  })
}

export function ByteConverter() {
  const [useBinary, setUseBinary] = useState(true)
  const [showBits, setShowBits] = useState(false)
  const [units, setUnits] = useState<UnitValue[]>(binaryUnits)
  const [bits, setBits] = useState<UnitValue[]>(bitUnits)
  const [copiedIndex, setCopiedIndex] = useState<string | null>(null)

  const updateAllUnits = useCallback((value: string, fromUnit: UnitValue) => {
    const numValue = parseFloat(value)

    if (!value || isNaN(numValue)) {
      setUnits((prev) => prev.map((u) => ({ ...u, value: "" })))
      setBits((prev) => prev.map((u) => ({ ...u, value: "" })))
      return
    }

    // Convert to bytes first
    const bytes = numValue * fromUnit.bytes

    // Update byte units
    setUnits((prev) =>
      prev.map((u) => ({
        ...u,
        value: u.bytes === fromUnit.bytes ? value : formatNumber(bytes / u.bytes),
      }))
    )

    // Update bit units
    setBits((prev) =>
      prev.map((u) => ({
        ...u,
        value: formatNumber(bytes / u.bytes),
      }))
    )
  }, [])

  const handleChange = (index: number, value: string, isBit: boolean = false) => {
    const unit = isBit ? bits[index] : units[index]
    updateAllUnits(value.replace(/,/g, ""), unit)
  }

  const handleUnitSystemChange = (binary: boolean) => {
    setUseBinary(binary)
    const newUnits = binary ? binaryUnits : decimalUnits
    setUnits(newUnits)
    // Clear values when switching
    setBits(bitUnits.map(u => ({ ...u, value: "" })))
  }

  const copyValue = async (key: string, value: string, label: string) => {
    if (!value) return
    await navigator.clipboard.writeText(value.replace(/,/g, ""))
    setCopiedIndex(key)
    setTimeout(() => setCopiedIndex(null), 2000)
    toast.success(`Copied ${label}`)
  }

  const clear = () => {
    setUnits((prev) => prev.map((u) => ({ ...u, value: "" })))
    setBits((prev) => prev.map((u) => ({ ...u, value: "" })))
  }

  const loadExample = () => {
    const exampleUnit = units.find(u => u.shortLabel === "GiB" || u.shortLabel === "GB")
    if (exampleUnit) {
      updateAllUnits("1", exampleUnit)
    }
  }

  // File size reference
  const fileSizes = [
    { name: "Text file (1 page)", size: "~2 KB" },
    { name: "MP3 song (4 min)", size: "~4 MB" },
    { name: "HD Photo", size: "~5 MB" },
    { name: "4K Video (1 min)", size: "~350 MB" },
    { name: "DVD", size: "4.7 GB" },
    { name: "Blu-ray", size: "25-50 GB" },
  ]

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Byte / Unit Converter</CardTitle>
          <CardDescription>
            Convert between bytes, kilobytes, megabytes, gigabytes, and more
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Options */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Switch
                id="binary"
                checked={useBinary}
                onCheckedChange={handleUnitSystemChange}
              />
              <Label htmlFor="binary">
                {useBinary ? "Binary (1024)" : "Decimal (1000)"}
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="bits"
                checked={showBits}
                onCheckedChange={setShowBits}
              />
              <Label htmlFor="bits">Show bits</Label>
            </div>

            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={loadExample}>
                Load Example (1 GB)
              </Button>
              <Button variant="outline" size="sm" onClick={clear}>
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>

          {/* Unit Inputs */}
          <div className="grid gap-3">
            {units.map((unit, index) => (
              <div
                key={unit.shortLabel}
                className="flex items-center gap-2"
              >
                <div className="w-24 text-sm font-medium text-right">
                  {unit.shortLabel}
                </div>
                <Input
                  type="text"
                  placeholder="0"
                  value={unit.value}
                  onChange={(e) => handleChange(index, e.target.value)}
                  className="font-mono flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 flex-shrink-0"
                  onClick={() => copyValue(`byte-${index}`, unit.value, unit.shortLabel)}
                  disabled={!unit.value}
                >
                  {copiedIndex === `byte-${index}` ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>

          {/* Bit Units */}
          {showBits && (
            <>
              <div className="border-t pt-4 mt-4">
                <Label className="text-sm font-medium mb-3 block">Bit Units</Label>
                <div className="grid gap-3">
                  {bits.map((unit, index) => (
                    <div
                      key={unit.shortLabel}
                      className="flex items-center gap-2"
                    >
                      <div className="w-24 text-sm font-medium text-right">
                        {unit.shortLabel}
                      </div>
                      <Input
                        type="text"
                        placeholder="0"
                        value={unit.value}
                        onChange={(e) => handleChange(index, e.target.value, true)}
                        className="font-mono flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 flex-shrink-0"
                        onClick={() => copyValue(`bit-${index}`, unit.value, unit.shortLabel)}
                        disabled={!unit.value}
                      >
                        {copiedIndex === `bit-${index}` ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Reference */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Binary vs Decimal</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-3">
            <div>
              <h4 className="font-medium">Binary (IEC) - Base 1024</h4>
              <p className="text-muted-foreground">
                KiB, MiB, GiB - Used by operating systems, RAM
              </p>
              <code className="text-xs">1 KiB = 1,024 bytes</code>
            </div>
            <div>
              <h4 className="font-medium">Decimal (SI) - Base 1000</h4>
              <p className="text-muted-foreground">
                KB, MB, GB - Used by storage manufacturers
              </p>
              <code className="text-xs">1 KB = 1,000 bytes</code>
            </div>
            <p className="text-muted-foreground mt-2">
              This is why a &quot;500 GB&quot; drive shows as ~465 GiB in your OS.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Common File Sizes</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-2">
              {fileSizes.map((item) => (
                <li key={item.name} className="flex justify-between">
                  <span className="text-muted-foreground">{item.name}</span>
                  <span className="font-mono">{item.size}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
